import ko from "knockout";
import config from "flow/config";
import MyFlow from "flow/MyFlow";
import { CloseCallback, IProductItem, ProductSelection, Dictionary } from "flow/api/types";
import internalComponentFactory from "flow/experimental/internalComponentFactory";
import environment from "flow/configuration/environment";
import manifestManager from "flow/utils/manifestManager";
import viewCache from "flow/utils/viewCache";
import clearSelectedProductFromUserSettings from "flow/utils/clearSelectedProductFromUserSettings";
import dataService from "flow/data/service";
import viewHtml from "./ProductSelectorDialog.html";
import { ICommand } from "flow/interfaces/ICommand";
import session from "flow/session/session";

export interface IProductSelectorDialogViewContext {
    products: IProductItem[];
    showCancel: boolean;
    defaultSelection: ProductSelection;
    fromUserSettings: boolean;
    reselect: boolean;
}

type ComponentConfig = {
    component: any;
    viewContext: IComponentViewContext;
};

interface IComponentViewContext {
    id: string;
    payload: Dictionary<any>;
    recenter: () => void;
    revalDim: () => void;
}

export default class ProductSelectorDialog {
    componentTitle = ko.observable();
    closeCallback: CloseCallback;
    products = ko.observableArray<IProductItem>();
    showCancel = ko.observable(false);
    selectedProduct = ko.observable<IProductItem>().extend({
        required: true,
        validation: {
            async: true,
            validator: function (item: IProductItem, _otherVal: any, callback: any) {
                callback({ isValid: !item.inactiveReason, message: item.inactiveReason });
            }
        }
    });
    remember = ko.observable<boolean>(false);
    canRemember = ko.pureComputed<boolean>(() => {
        if (this.selectedProduct()) {
            const componentConfig = this.components().find(c => c.component.manifest.id === this.selectedProduct().id);
            if (componentConfig) {
                return componentConfig.component.canRemember();
            }
            return this.selectedProduct().external === false; // Only allow remembering if it is not an external product, since we don't want to store Flow 3 products in Flow 2 user settings
        }
        return false;
    });
    buttonCommands = ko.observableArray();
    // This one won't run through command adapter, so make sure to include all fields that are used in the view
    logoutCommand: ICommand = {
        click: () => {
            session.forceLogout(true);
        },
        text: this.myFlow.translate("FlowHtml.Translations.LogOut"),
        isVisible: ko.pureComputed(() => !this.showCancel())
    };

    components = ko.observableArray<ComponentConfig>([]);

    optionsCaption = "- " + this.myFlow.translate("FlowHtml.Translations.Select") + " -";

    hasSavedChoice = ko.observable<boolean>(false);

    private recenter: () => void;

    constructor(private readonly myFlow: MyFlow) {
        // These won't run through command adapter, so make sure to include all fields that are used in the view
        this.buttonCommands([
            {
                text: this.myFlow.translate("FlowHtml.Translations.Cancel"),
                click: this.cancel,
                isVisible: this.showCancel,
                buttonClass: "btn-default"
            },
            {
                text: this.myFlow.translate("FlowHtml.Translations.Continue"),
                click: this.select,
                isVisible: true,
                buttonClass: "btn-success"
            }
        ]);

        this.componentTitle(this.myFlow.translate("FlowHtml.Translations.HowDoYouWantToContinue"));
    }

    activate = (
        viewContext: IProductSelectorDialogViewContext,
        closeCallback: CloseCallback,
        revalDim: () => void,
        recenter: () => void
    ) => {
        const defaultId = viewContext.defaultSelection ? viewContext.defaultSelection.id : undefined;
        this.recenter = recenter;

        this.selectedProduct.subscribe(newProduct => {
            this.myFlow.clearLocalNotification();

            if (
                newProduct &&
                !newProduct.inactiveReason &&
                newProduct.manifest &&
                newProduct.manifest.customOptionsComponent &&
                !this.components().some(c => c.component.manifest.id === newProduct.id)
            ) {
                const component = internalComponentFactory.createComponentFromManifest(
                    this.myFlow,
                    newProduct.manifest.customOptionsComponent
                );
                this.components.push({
                    component: component,
                    viewContext: {
                        id: newProduct.id,
                        payload: newProduct.id === defaultId ? viewContext.defaultSelection.payload : undefined,
                        recenter: recenter,
                        revalDim: revalDim
                    }
                });
            }

            window.requestAnimationFrame(this.recenter);
        });

        const uiCulture = environment.getUiCulture();
        viewContext.products.sort((a: IProductItem, b: IProductItem) => a.name.localeCompare(b.name, uiCulture));

        this.products(viewContext.products);
        this.showCancel(!!viewContext.showCancel);
        this.remember(viewContext.defaultSelection ? viewContext.defaultSelection.remember : false);
        this.closeCallback = closeCallback;

        this.prefillIfPreviousSelectionOrOnlyOneChoice(defaultId);

        if (viewContext.reselect) {
            // Reselection, fromUserSettings could be stale, need fresh value
            dataService.hasSelectedProduct(this.myFlow).then(this.hasSavedChoice);
        } else {
            this.hasSavedChoice(viewContext.fromUserSettings);
        }
    };

    private prefillIfPreviousSelectionOrOnlyOneChoice(defaultId: string) {
        if (defaultId) {
            const product = this.products().find(p => p.id === defaultId);
            if (product) {
                this.selectedProduct(product);
                return;
            }
        }

        this.prefillIfOnlyOneChoice();
    }

    private prefillIfOnlyOneChoice() {
        // If there is only one choice, preselect in dropdown
        if (this.products().length === 1) {
            const product = this.products()[0];
            if (product) {
                this.selectedProduct(product);
            }
        }
    }

    private select = () => {
        const product = this.selectedProduct();

        const errors = this.myFlow.getValidationErrorsV3(this.selectedProduct);
        if (errors().length > 0) {
            errors.showAllMessages(true);
            return;
        }

        const componentConfig = this.components().find(c => c.component.manifest.id === product.id);

        if (componentConfig) {
            if (!this.isComponentValid(componentConfig.component)) {
                return;
            }
        }

        const productSelection: ProductSelection = {
            id: product.id,
            payload: componentConfig?.component?.viewModel()?.getConfigObject() ?? undefined,
            remember: this.canRemember() && this.remember(),
            external: product.external,
            valid: false
        };

        this.closeCallback(productSelection);
    };

    private isComponentValid = (component: any) => {
        const viewModel = component.viewModel();
        if (!viewModel || !viewModel.validatableObject) {
            return true;
        }

        const errors = component.myFlow.getValidationErrorsV3(viewModel.validatableObject);
        if (errors().length === 0) {
            return true;
        } else {
            errors.showAllMessages();
            component.myFlow.logValidationErrorsV3(viewModel.validatableObject);
            return false;
        }
    };

    private cancel = () => {
        this.closeCallback();
    };

    getDimensions = (maxWidth: number) => {
        return {
            width: Math.min(maxWidth - 24, 500),
            height: "auto"
        };
    };

    onForget = () => {
        clearSelectedProductFromUserSettings(this.myFlow).then(() => {
            this.hasSavedChoice(false);
            this.myFlow.clearValidationErrorsV3(this.selectedProduct);
        });
    };
}

// I create my own manifest
manifestManager.addManifests(config.moduleId, [
    {
        id: config.componentIds.productSelectorDialog,
        isDialog: true,
        reuseInstance: false,
        useFormValidation: false,
        name: "",
        namespace: "default",
        viewModelId: "flow/viewModels/ProductSelectorDialog",
        viewUrl: "virtual/ProductSelectorDialog"
    }
]);

viewCache.inject("virtual/ProductSelectorDialog", viewHtml);
